<template>
  <div class="posts-tab">
    <!-- Modern Header -->
    <div class="tab-header">
      <div class="header-content">
        <div class="header-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
        <div>
          <h2>Gerenciamento de Posts</h2>
          <p>Controle posts publicados e moderação de conteúdo</p>
        </div>
      </div>
      <div class="header-stats">
        <div class="stat-item">
          <span class="stat-value">{{ pagination?.total || 0 }}</span>
          <span class="stat-label">Total</span>
        </div>
      </div>
    </div>

    <!-- Search and Filters -->
    <div class="filters-section">
      <div class="search-bar">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="search-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Buscar por título..."
          class="search-input"
          @input="debounceSearch"
        />
        <button v-if="filters.search" @click="clearSearch" class="clear-btn">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div class="filter-groups">
        <div class="filter-group">
          <span class="filter-label">Status:</span>
          <div class="filter-buttons">
            <button
              v-for="status in ['all', 'published', 'unpublished']"
              :key="status"
              @click="setStatusFilter(status)"
              :class="['filter-btn', { active: filters.published === status }]"
            >
              <svg
                v-if="status === 'published'"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else-if="status === 'unpublished'"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 9 11.317A11.954 11.954 0 0117.834 7c.11-.681.166-1.351.166-2A11.954 11.954 0 0112 1.944a11.954 11.954 0 01-2-.135V9a1 1 0 11-2 0V1.809A11.937 11.937 0 0110 1.944z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"
                />
              </svg>
              <span>{{
                status === "all"
                  ? "Todos"
                  : status === "published"
                  ? "Publicados"
                  : "Ocultos"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Posts Grid -->
    <div class="posts-grid" v-if="!loading && posts.length > 0">
      <div
        v-for="post in posts"
        :key="post.id"
        class="post-card"
      >
        <!-- Post Image & Content -->
        <div class="post-header">
          <div v-if="post.image_url" class="post-image-wrapper">
            <img
              :src="post.image_url"
              :alt="post.title"
              class="post-image"
              @error="handleImageError"
            />
            <div class="post-status-badge" :class="post.published ? 'published' : 'unpublished'">
              <svg
                v-if="post.published"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 9 11.317A11.954 11.954 0 0117.834 7c.11-.681.166-1.351.166-2A11.954 11.954 0 0112 1.944a11.954 11.954 0 01-2-.135V9a1 1 0 11-2 0V1.809A11.937 11.937 0 0110 1.944z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ post.published ? "Publicado" : "Oculto" }}</span>
            </div>
          </div>
          <div class="post-content">
            <h3 class="post-title">{{ post.title }}</h3>
            <p class="post-excerpt">{{ truncateText(post.content, 120) }}</p>
          </div>
        </div>

        <!-- Post Meta Info -->
        <div class="post-meta">
          <div class="meta-row">
            <span class="meta-label">Tema:</span>
            <span class="theme-badge">{{ post.theme || "Sem tema" }}</span>
          </div>

          <div class="meta-row">
            <span class="meta-label">Autor:</span>
            <span class="author-name">{{ post.author_username }}</span>
          </div>

          <div class="meta-row">
            <span class="meta-label">Engajamento:</span>
            <div class="engagement">
              <div class="engagement-item">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                </svg>
                <span>{{ post.likes_count || 0 }}</span>
              </div>
              <div class="engagement-item">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2m-6 8v-2H8v2h4m2-4v2h-2V9h2M6 9h2v2H6V9" clip-rule="evenodd" />
                </svg>
                <span>{{ post.comments_count || 0 }}</span>
              </div>
            </div>
          </div>

          <div class="meta-row">
            <span class="meta-label">Criado em:</span>
            <span class="meta-value">{{ formatDate(post.created_at) }}</span>
          </div>
        </div>

        <!-- Post Actions -->
        <div class="post-actions">
          <button
            @click="openEditModal(post)"
            class="action-btn edit-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            <span>Editar</span>
          </button>

          <button
            v-if="!post.published"
            @click="togglePostStatus(post, true)"
            class="action-btn publish-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>Publicar</span>
          </button>

          <button
            v-else
            @click="openUnpublishModal(post)"
            class="action-btn unpublish-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.477 3 10 3c-2.25 0-4.392.73-6.243 2.008L3.707 2.293zM6.419 7.51a1 1 0 011.414 0c1.542 1.541 1.542 4.038 0 5.578a1 1 0 01-1.414-1.414c.701-.7 1.833-1.833 1.833-2.145 0-.312-1.132-1.444-1.833-2.145a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <span>Ocultar</span>
          </button>

          <button
            @click="openDeleteModal(post)"
            class="action-btn delete-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2H6.618l-.894-1.447A1 1 0 005 4H1a1 1 0 000 2h8v10H7V8a1 1 0 10-2 0v8H9V8a1 1 0 102 0v8h1V6h2V8a1 1 0 102 0v6h2V6H6z" clip-rule="evenodd" />
            </svg>
            <span>Deletar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && posts.length === 0" class="empty-state">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
      <h3>Nenhum post encontrado</h3>
      <p>Tente ajustar os filtros de busca</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>Carregando posts...</p>
    </div>

    <!-- Pagination -->
    <div v-if="!loading && pagination" class="pagination-section">
      <button
        @click="prevPage"
        :disabled="pagination.page <= 1"
        class="page-btn"
      >
        ← Anterior
      </button>

      <span class="page-info">
        Página {{ pagination.page }} de
        {{ Math.ceil(pagination.total / pagination.limit) }} ({{
          pagination.total
        }}
        posts)
      </span>

      <button
        @click="nextPage"
        :disabled="
          pagination.page >= Math.ceil(pagination.total / pagination.limit)
        "
        class="page-btn"
      >
        Próxima →
      </button>
    </div>

    <!-- Edit Modal -->
    <transition name="modal-fade">
      <div v-if="editModal.visible" class="modal-overlay" @click="closeEditModal">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <div class="modal-icon edit-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <div class="modal-title-section">
              <h3 class="modal-title">Editar Post</h3>
              <p class="modal-subtitle">{{ editModal.post?.title }}</p>
            </div>
            <button @click="closeEditModal" class="modal-close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form @submit.prevent="savePost" class="modal-body">
            <div class="form-group">
              <label class="form-label">Título *</label>
              <input
                v-model="editForm.title"
                type="text"
                class="form-input"
                :class="{ 'input-error': validationErrors.title }"
                placeholder="Digite o título do post"
              />
              <span v-if="validationErrors.title" class="error-message">
                {{ validationErrors.title }}
              </span>
            </div>

            <div class="form-group">
              <label class="form-label">Conteúdo *</label>
              <textarea
                v-model="editForm.content"
                rows="8"
                class="form-textarea"
                :class="{ 'input-error': validationErrors.content }"
                placeholder="Digite o conteúdo do post..."
              ></textarea>
              <span v-if="validationErrors.content" class="error-message">
                {{ validationErrors.content }}
              </span>
            </div>

            <div class="form-group">
              <label class="form-label">Tema</label>
              <input
                v-model="editForm.theme"
                type="text"
                class="form-input"
                placeholder="Digite o tema (opcional)"
              />
            </div>

            <div class="form-group">
              <label class="form-label">URL da Imagem</label>
              <input
                v-model="editForm.image_url"
                type="url"
                class="form-input"
                placeholder="https://exemplo.com/imagem.jpg"
              />
            </div>
          </form>

          <div class="modal-footer">
            <button @click="closeEditModal" class="btn-secondary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Cancelar</span>
            </button>
            <button @click="savePost" :disabled="saving" class="btn-primary">
              <svg
                v-if="saving"
                class="animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ saving ? "Salvando..." : "Salvar Alterações" }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Unpublish Modal -->
    <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="unpublishModal.visible" class="modal-overlay" @click="closeUnpublishModal">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <div class="modal-icon unpublish-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div class="modal-title-section">
              <h3 class="modal-title">Ocultar Post</h3>
              <p class="modal-subtitle">{{ unpublishModal.post?.title }}</p>
            </div>
            <button @click="closeUnpublishModal" class="modal-close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="modal-message warning">
              <p>
                ⚠️ Você está prestes a ocultar este post. Ele não será mais visível
                para os usuários do blog.
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">Motivo da moderação (opcional)</label>
              <textarea
                v-model="unpublishReason"
                rows="3"
                class="form-textarea"
                placeholder="Explique o motivo pelo qual este post está sendo oculto..."
              ></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button @click="closeUnpublishModal" class="btn-secondary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Cancelar</span>
            </button>
            <button @click="confirmUnpublish" :disabled="unpublishing" class="btn-primary btn-warning">
              <svg
                v-if="unpublishing"
                class="animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.477 3 10 3c-2.25 0-4.392.73-6.243 2.008L3.707 2.293z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ unpublishing ? "Ocultando..." : "Confirmar Ocultação" }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
    </Teleport>

    <!-- Delete Modal -->
    <Teleport to="body">
    <transition name="modal-fade">
      <div v-if="deleteModal.visible" class="modal-overlay" @click="closeDeleteModal">
        <div class="modal-container" @click.stop>
          <div class="modal-header">
            <div class="modal-icon delete-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </div>
            <div class="modal-title-section">
              <h3 class="modal-title">Deletar Post</h3>
              <p class="modal-subtitle">{{ deleteModal.post?.title }}</p>
            </div>
            <button @click="closeDeleteModal" class="modal-close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <div class="modal-message danger">
              <p>
                🗑️ Você está prestes a deletar permanentemente este post.
                Esta ação não pode ser desfeita e todos os comentários e likes
                associados serão perdidos.
              </p>
            </div>

            <div class="delete-preview">
              <div class="preview-stats">
                <div class="stat">
                  <span class="stat-number">{{ deleteModal.post?.likes_count || 0 }}</span>
                  <span class="stat-label">Likes</span>
                </div>
                <div class="stat">
                  <span class="stat-number">{{ deleteModal.post?.comments_count || 0 }}</span>
                  <span class="stat-label">Comentários</span>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button @click="closeDeleteModal" class="btn-secondary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>Cancelar</span>
            </button>
            <button @click="confirmDelete" :disabled="deleting" class="btn-primary btn-danger">
              <svg
                v-if="deleting"
                class="animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"
                  clip-rule="evenodd"
                />
                <path
                  fill-rule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1zM5 10a1 1 0 011 1v1h1a1 1 0 102 0H6v-1a1 1 0 01-1-1zM7 10a1 1 0 011 1v1h2a1 1 0 102 0h-2v-1a1 1 0 01-1-1zM9 14a1 1 0 100 2h6a1 1 0 100-2H9zM9 17a1 1 0 100 2h6a1 1 0 100-2H9zM2 7a1 1 0 011-1h5.5a1 1 0 100-2H3a1 1 0 01-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
              <span>{{ deleting ? "Deletando..." : "Confirmar Deletar" }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
    </Teleport>
  </div>
</template>

<script>
import apiService from "@/services/api";
import { debounce } from "lodash";

export default {
  name: "PostsTab",
  data() {
    return {
      posts: [],
      loading: false,
      pagination: null,
      filters: {
        search: "",
        published: "all",
      },
      debounceSearch: null,

      // Modals
      editModal: {
        visible: false,
        post: null,
      },
      editForm: {
        title: "",
        content: "",
        theme: "",
        image_url: "",
      },
      validationErrors: {},
      saving: false,

      unpublishModal: {
        visible: false,
        post: null,
      },
      unpublishReason: "",
      unpublishing: false,

      deleteModal: {
        visible: false,
        post: null,
      },
      deleting: false,
    };
  },
  mounted() {
    this.debounceSearch = debounce(this.loadPosts, 500);
    this.loadPosts();
  },
  methods: {
    clearSearch() {
      this.filters.search = "";
      this.loadPosts();
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },

    handleImageError(event) {
      event.target.style.display = "none";
    },

    getStatusBadgeClass(published) {
      return published ? "published" : "unpublished";
    },

    setStatusFilter(status) {
      this.filters.published = status;
      this.loadPosts();
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    },

    async loadPosts() {
      this.loading = true;
      try {
        const params = {
          search: this.filters.search || undefined,
          published: this.filters.published === "all" ? undefined : this.filters.published,
          page: this.pagination?.page || 1,
          limit: 12,
        };

        const response = await apiService.get("/api/handlers", {
          params: { module: "admin", action: "posts", ...params },
        });
        this.posts = response.data?.posts || response.posts || [];
        this.pagination = response.data?.pagination || response.pagination || null;
      } catch (error) {
        console.error("Erro ao carregar posts:", error);
        this.$toast?.error("Erro ao carregar posts");
      } finally {
        this.loading = false;
      }
    },

    prevPage() {
      if (this.pagination && this.pagination.page > 1) {
        this.pagination.page--;
        this.loadPosts();
      }
    },

    nextPage() {
      if (
        this.pagination &&
        this.pagination.page < Math.ceil(this.pagination.total / this.pagination.limit)
      ) {
        this.pagination.page++;
        this.loadPosts();
      }
    },

    // Modal Edit Methods
    openEditModal(post) {
      this.editModal = {
        visible: true,
        post: post,
      };
      this.editForm = {
        title: post.title,
        content: post.content,
        theme: post.theme || "",
        image_url: post.image_url || "",
      };
      this.validationErrors = {};
      this.saving = false;
    },

    closeEditModal() {
      this.editModal.visible = false;
      this.editModal.post = null;
      this.validationErrors = {};
      this.saving = false;
    },

    async savePost() {
      // Validation
      this.validationErrors = {};
      if (!this.editForm.title?.trim()) {
        this.validationErrors.title = "Título é obrigatório";
      }
      if (!this.editForm.content?.trim()) {
        this.validationErrors.content = "Conteúdo é obrigatório";
      }

      if (Object.keys(this.validationErrors).length > 0) {
        return;
      }

      this.saving = true;
      try {
        await apiService.put(`/api/handlers`, {
          title: this.editForm.title.trim(),
          content: this.editForm.content.trim(),
          theme: this.editForm.theme.trim() || null,
          image_url: this.editForm.image_url.trim() || null,
        }, {
          params: { module: "admin", action: "posts", postId: this.editModal.post.id },
        });

        this.$toast?.success("Post editado com sucesso");
        this.closeEditModal();
        this.loadPosts(); // Reload posts
      } catch (error) {
        console.error("Erro ao salvar post:", error);
        this.$toast?.error(error.response?.data?.message || "Erro ao salvar post");
      } finally {
        this.saving = false;
      }
    },

    // Modal Unpublish Methods
    openUnpublishModal(post) {
      this.unpublishModal = {
        visible: true,
        post: post,
      };
      this.unpublishReason = "";
      this.unpublishing = false;
    },

    closeUnpublishModal() {
      this.unpublishModal.visible = false;
      this.unpublishModal.post = null;
      this.unpublishReason = "";
      this.unpublishing = false;
    },

    async confirmUnpublish() {
      this.unpublishing = true;
      try {
        await apiService.post(`/api/handlers`, {
          reason: this.unpublishReason.trim() || null,
        }, {
      params: { module: "admin", action: "posts-toggle-published", postId: this.unpublishModal.post.id },
        });

        this.$toast?.success("Post ocultado com sucesso");
        this.closeUnpublishModal();
        this.loadPosts(); // Reload posts
      } catch (error) {
        console.error("Erro ao ocultar post:", error);
        this.$toast?.error(error.response?.data?.message || "Erro ao ocultar post");
      } finally {
        this.unpublishing = false;
      }
    },

    // Quick Publish Method
    async togglePostStatus(post, publish) {
      try {
        await apiService.post(`/api/handlers`, {
          reason: publish ? null : "Publicação rápida",
        }, {
          params: { module: "admin", action: "posts-toggle-published", postId: post.id },
        });

        const action = publish ? "publicado" : "ocultado";
        this.$toast?.success(`Post ${action} com sucesso`);
        this.loadPosts(); // Reload posts
      } catch (error) {
        console.error("Erro ao alterar status do post:", error);
        this.$toast?.error(error.response?.data?.message || "Erro ao alterar status");
      }
    },

    // Modal Delete Methods
    openDeleteModal(post) {
      this.deleteModal = {
        visible: true,
        post: post,
      };
      this.deleting = false;
    },

    closeDeleteModal() {
      this.deleteModal.visible = false;
      this.deleteModal.post = null;
      this.deleting = false;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        await apiService.post(`/api/handlers`, null, {
          params: { module: "admin", action: "posts-delete", postId: this.deleteModal.post.id },
        });

        this.$toast?.success("Post deletado com sucesso");
        this.closeDeleteModal();
        this.loadPosts(); // Reload posts
      } catch (error) {
        console.error("Erro ao deletar post:", error);
        this.$toast?.error(error.response?.data?.message || "Erro ao deletar post");
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Modern Posts Tab Styles */
.posts-tab {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.tab-header {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.header-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.header-icon svg {
  width: 32px;
  height: 32px;
  color: white;
}

.tab-header h2 {
  font-size: 1.875rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.tab-header p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.header-stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 1.5rem;
  background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e5e7eb;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: #3b82f6;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}

/* Filters Section */
.filters-section {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
}

.search-bar {
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.9375rem;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.clear-btn svg {
  width: 16px;
  height: 16px;
}

.filter-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  min-width: 80px;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-btn svg {
  width: 16px;
  height: 16px;
}

.filter-btn:hover {
  background: white;
  border-color: #d1d5db;
  color: #374151;
}

.filter-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

/* Posts Grid */
.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.post-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: slideIn 0.4s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.post-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border-color: #3b82f6;
}

/* Post Header */
.post-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.post-image-wrapper {
  position: relative;
  flex-shrink: 0;
}

.post-image {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  object-fit: cover;
  border: 2px solid #e5e7eb;
}

.post-status-badge {
  position: absolute;
  bottom: -5px;
  right: -5px;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border: 2px solid white;
}

.post-status-badge.published {
  background: #d1fae5;
  color: #065f46;
}

.post-status-badge.unpublished {
  background: #fef3c7;
  color: #92400e;
}

.post-status-badge svg {
  width: 12px;
  height: 12px;
}

.post-content {
  flex: 1;
  min-width: 0;
}

.post-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.post-excerpt {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Post Meta */
.post-meta {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.meta-label {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 500;
}

.meta-value {
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
  text-align: right;
}

.theme-badge {
  background: #e0e7ff;
  color: #3730a3;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.engagement {
  display: flex;
  gap: 1rem;
}

.engagement-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 500;
}

.engagement-item svg {
  width: 14px;
  height: 14px;
}

/* Post Actions */
.post-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-btn svg {
  width: 16px;
  height: 16px;
}

.edit-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.publish-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.publish-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.unpublish-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.unpublish-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
}

.delete-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.delete-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

/* Empty State */
.empty-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 2px dashed #e5e7eb;
}

.empty-state svg {
  width: 64px;
  height: 64px;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  font-size: 0.9375rem;
  color: #9ca3af;
  margin: 0;
}

/* Loading State */
.loading-state {
  background: white;
  border-radius: 16px;
  padding: 4rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.page-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.page-info {
  font-size: 0.9375rem;
  color: #6b7280;
  font-weight: 500;
}

/* Modal Styles */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from .modal-container {
  transform: scale(0.9) translateY(20px);
}

.modal-fade-leave-to .modal-container {
  transform: scale(0.95) translateY(-20px);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 2rem;
  border-bottom: 1px solid #f3f4f6;
}

.modal-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.modal-icon svg {
  width: 28px;
  height: 28px;
  color: white;
}

.modal-icon.edit-icon,
.modal-container .modal-icon:not(.unpublish-icon):not(.delete-icon) {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.modal-icon.unpublish-icon {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.modal-icon.delete-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.modal-title-section {
  flex: 1;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.modal-subtitle {
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.modal-close svg {
  width: 20px;
  height: 20px;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 0.875rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-family: inherit;
  transition: all 0.3s ease;
  background: #f9fafb;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.input-error,
.form-textarea.input-error {
  border-color: #ef4444;
  background: #fef2f2;
}

.error-message {
  font-size: 0.8125rem;
  color: #ef4444;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

/* Modal Messages */
.modal-message {
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border-left: 4px solid;
  margin-bottom: 1.5rem;
}

.modal-message.warning {
  background: #fef3c7;
  border-color: #f59e0b;
}

.modal-message.danger {
  background: #fef2f2;
  border-color: #ef4444;
}

.modal-message p {
  font-size: 0.9375rem;
  color: #374151;
  margin: 0;
  line-height: 1.6;
}

/* Delete Preview */
.delete-preview {
  margin-top: 1.5rem;
}

.preview-stats {
  display: flex;
  gap: 2rem;
  justify-content: center;
}

.preview-stats .stat {
  text-align: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 10px;
  min-width: 100px;
}

.preview-stats .stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 800;
  color: #ef4444;
}

.preview-stats .stat-label {
  font-size: 0.8125rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  gap: 0.75rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #f3f4f6;
}

.btn-secondary,
.btn-primary {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary {
  background: #f3f4f6;
  color: #6b7280;
}

.btn-secondary:hover {
  background: #e5e7eb;
  color: #374151;
}

.btn-secondary svg {
  width: 18px;
  height: 18px;
}

.btn-primary {
  color: white;
}

.btn-primary svg {
  width: 18px;
  height: 18px;
}

.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.btn-warning:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  transform: translateY(-1px);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.btn-danger:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Responsive */
@media (max-width: 1024px) {
  .posts-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }

  .header-stats {
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .tab-header {
    flex-direction: column;
    gap: 1.5rem;
  }

  .header-stats {
    width: 100%;
    justify-content: center;
  }

  .posts-grid {
    grid-template-columns: 1fr;
  }

  .filter-groups {
    flex-direction: column;
  }

  .filter-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .pagination-section {
    flex-direction: column;
    gap: 1rem;
  }

  .modal-container {
    margin: 0;
  }

  .preview-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .preview-stats .stat {
    min-width: auto;
  }
}
</style>
